/**
 * Seeded-key overlay for workflow step titles.
 *
 * Compiled H2 headings stay the source of truth. This table localizes the
 * known `morning-digest` keys for the nine content languages. Unknown
 * templates, unknown keys, and missing locales fall back to the heading.
 *
 * One table, imported by backend intro and frontend.
 * Live run / intro: pass thread (answer) language.
 * Editor canvas and step list: pass the UI language, since a house seed's
 * titles are product chrome there; custom templates fall back to H2.
 * Prompt bodies stay English.
 */

export const WORKFLOW_STEP_TITLE_LANGUAGES = [
  "en",
  "es",
  "fr",
  "de",
  "pt",
  "it",
  "nl",
  "tr",
  "ja",
] as const;

export type WorkflowStepTitleLanguage =
  (typeof WORKFLOW_STEP_TITLE_LANGUAGES)[number];

const MORNING_DIGEST_STEP_KEYS = [
  "sweep_news",
  "review_sweep",
  "write_brief",
  "review_brief",
  "draft_content",
  "review_gate",
  "derive_questions",
  "review_questions",
  "twin_test",
  "daily_report",
  "review_report",
  "write_slack_post",
  "post_to_slack",
] as const;

type MorningDigestStepKey = (typeof MORNING_DIGEST_STEP_KEYS)[number];

type TitleByLanguage = Record<WorkflowStepTitleLanguage, string>;

const MORNING_DIGEST_STEP_TITLES: Record<MorningDigestStepKey, TitleByLanguage> =
  {
    sweep_news: {
      en: "News research",
      es: "Investigación de noticias",
      fr: "Recherche d'actualités",
      de: "Nachrichtenrecherche",
      pt: "Pesquisa de notícias",
      it: "Ricerca di notizie",
      nl: "Nieuwsresearch",
      tr: "Haber araştırması",
      ja: "ニュース調査",
    },
    review_sweep: {
      en: "Review the scan",
      es: "Revisar el rastreo",
      fr: "Relire le scan",
      de: "Den Scan prüfen",
      pt: "Revisar a varredura",
      it: "Rivedere la scansione",
      nl: "De scan beoordelen",
      tr: "Taramayı incele",
      ja: "スキャンを確認",
    },
    write_brief: {
      en: "Write the morning brief",
      es: "Escribir el brief de la mañana",
      fr: "Rédiger le brief du matin",
      de: "Das Morning Brief schreiben",
      pt: "Escrever o brief da manhã",
      it: "Scrivere il brief del mattino",
      nl: "De ochtendbrief schrijven",
      tr: "Sabah brifingini yaz",
      ja: "朝のブリーフを書く",
    },
    review_brief: {
      en: "Review the brief",
      es: "Revisar el brief",
      fr: "Relire le brief",
      de: "Das Brief prüfen",
      pt: "Revisar o brief",
      it: "Rivedere il brief",
      nl: "De brief beoordelen",
      tr: "Brifingi incele",
      ja: "ブリーフを確認",
    },
    draft_content: {
      en: "Draft content variants",
      es: "Borradores de contenido",
      fr: "Variantes de contenu",
      de: "Inhaltsvarianten entwerfen",
      pt: "Variantes de conteúdo",
      it: "Varianti di contenuto",
      nl: "Contentvarianten opstellen",
      tr: "İçerik varyantları tasarla",
      ja: "コンテンツ案を作成",
    },
    review_gate: {
      en: "Review the drafts",
      es: "Revisar los borradores",
      fr: "Relire les brouillons",
      de: "Die Entwürfe prüfen",
      pt: "Revisar os rascunhos",
      it: "Rivedere le bozze",
      nl: "De concepten beoordelen",
      tr: "Taslakları incele",
      ja: "下書きを確認",
    },
    derive_questions: {
      en: "Prepare twin questions",
      es: "Preparar las preguntas para twins",
      fr: "Préparer les questions pour les twins",
      de: "Twin-Fragen vorbereiten",
      pt: "Preparar as perguntas para twins",
      it: "Preparare le domande per i twin",
      nl: "Twinvragen voorbereiden",
      tr: "Twin sorularını hazırla",
      ja: "ツインの質問を準備",
    },
    review_questions: {
      en: "Review the twin questions",
      es: "Revisar las preguntas para twins",
      fr: "Relire les questions pour les twins",
      de: "Die Twin-Fragen prüfen",
      pt: "Revisar as perguntas para twins",
      it: "Rivedere le domande per i twin",
      nl: "De twinvragen beoordelen",
      tr: "Twin sorularını incele",
      ja: "ツインの質問を確認",
    },
    twin_test: {
      en: "Test with twins",
      es: "Probar con twins",
      fr: "Tester avec les twins",
      de: "Mit Twins testen",
      pt: "Testar com twins",
      it: "Testare con i twin",
      nl: "Testen met twins",
      tr: "Twinlerle test et",
      ja: "ツインでテスト",
    },
    daily_report: {
      en: "Draft the report",
      es: "Redactar el informe",
      fr: "Rédiger le rapport",
      de: "Den Bericht schreiben",
      pt: "Redigir o relatório",
      it: "Scrivere il report",
      nl: "Het rapport schrijven",
      tr: "Raporu yaz",
      ja: "レポートを作成",
    },
    review_report: {
      en: "Review the report",
      es: "Revisar el informe",
      fr: "Relire le rapport",
      de: "Den Bericht prüfen",
      pt: "Revisar o relatório",
      it: "Rivedere il report",
      nl: "Het rapport beoordelen",
      tr: "Raporu incele",
      ja: "レポートを確認",
    },
    write_slack_post: {
      en: "Write the Slack post",
      es: "Escribir el post de Slack",
      fr: "Rédiger le post Slack",
      de: "Den Slack-Post schreiben",
      pt: "Escrever o post do Slack",
      it: "Scrivere il post per Slack",
      nl: "Het Slack-bericht schrijven",
      tr: "Slack gönderisini yaz",
      ja: "Slack投稿を書く",
    },
    post_to_slack: {
      en: "Post to Slack",
      es: "Publicar en Slack",
      fr: "Publier sur Slack",
      de: "In Slack posten",
      pt: "Publicar no Slack",
      it: "Pubblicare su Slack",
      nl: "Plaatsen op Slack",
      tr: "Slack'e gönder",
      ja: "Slackに投稿",
    },
  };

export const WORKFLOW_STEP_TITLE_TABLE: Record<
  string,
  Record<string, TitleByLanguage>
> = {
  "morning-digest": MORNING_DIGEST_STEP_TITLES,
};

export interface LocalizedWorkflowStepTitleInput {
  templateSlug?: string | null;
  stepKey?: string | null;
  language?: string | null;
  /** Compiled H2 heading. Used when the overlay has no row. */
  fallback: string;
}

function langCode(language: string | null | undefined): string {
  return (language ?? "en").trim().toLowerCase().split("-")[0] || "en";
}

/**
 * Localized title for a seeded workflow step, or the compiled heading.
 */
export function localizedWorkflowStepTitle(
  input: LocalizedWorkflowStepTitleInput,
): string {
  const { templateSlug, stepKey, language, fallback } = input;
  const slug = (templateSlug ?? "").trim();
  const key = (stepKey ?? "").trim();
  const byLang = WORKFLOW_STEP_TITLE_TABLE[slug]?.[key];
  if (!byLang) {
    return fallback;
  }
  const overlay = byLang[langCode(language) as WorkflowStepTitleLanguage] ?? byLang.en;
  if (typeof overlay === "string" && overlay.trim().length > 0) {
    return overlay;
  }
  return fallback;
}
